import { LitElement, css, html } from "lit";
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { angleLeft, angleRight } from "@cz/icons/dist/cz/iconSprites.mjs";
import { trapFocus } from '../helpers/trap-focus.js';

export class CzSidepanel extends LitElement {
	static styles = css`
		@layer component {
			:host {
				--_cz-sidepanel-backdrop-padding: var(--cz-sidepanel-backdrop-padding, var(--sidepanel-backdrop-padding, 1rem));
				--_cz-sidepanel-backdrop-color: var(--cz-sidepanel-backdrop-color, var(--sidepanel-backdrop-color, black));
				
				--_cz-sidepanel-background-color: var(--cz-sidepanel-background-color, var(--sidepanel-background-color, canvas));
				--_cz-sidepanel-text-color: var(--cz-sidepanel-text-color, var(--sidepanel-text-color, canvastext));
				--_cz-sidepanel-width: min(max(var(--cz-sidepanel-width), 26rem), calc(100vi - var(--_cz-sidepanel-backdrop-padding, 1rem)));
				--_cz-sidepanel-transition-duration: var(--cz-sidepanel-transition-duration, var(--sidepanel-transition-duration, 0.25s));
				--_cz-sidepanel-transition-timing: var(--cz-sidepanel-transition-timing, var(--sidepanel-transition-timing, ease-in-out));
				--_cz-sidepanel-content-padding-block: var(--cz-sidepanel-content-padding-block, var(--sidepanel-content-padding-block, 0px 2rem));
				--_cz-sidepanel-content-padding-inline: var(--cz-sidepanel-content-padding-inline, var(--sidepanel-content-padding-inline, 1rem));

				--cz-sidepanel-width: var(--sidepanel-width, 26rem);
				--close-button-text-color-hover: var(--color-primary-bird, #333333);
				--close-button-border-color: var(--color-secondary-lion,#989691);

				--focus-ring-color: var(--a11y-focus-ring-color, var(--color-tertiary-otter, #0071b2));
			}

			.drawer,
			.drawer[open].closing {
				--_translate-inline: var(--translate-inline, var(--_cz-sidepanel-width));
				--_sidepanel-onleft: var(--sidepanel-onleft, unset);
				--_sidepanel-onright: var(--sidepanel-onright, 100vi);
				/* Remove default dialog styling */
				margin: 0;
				padding: 0;
				max-height: unset;
				border: none;
				
				height: 100%;
				width: var(--_cz-sidepanel-width);

				position: fixed;
				top: 0;
				
				left: var(--_sidepanel-onright);
				translate: var(--_translate-inline) 0px;

				color: inherit;
				box-sizing: border-box;
				overflow-y: auto;
				overscroll-behavior: contain;
				container-type: inline-size;
				
				background-color: var(--_cz-sidepanel-background-color, white);
				max-width: var(--_cz-sidepanel-width, 100%);
				
				> .inner {
					position: relative;
					inset: 0;
					display: flex;
					flex-direction: column;
					width: 100cqi;
					max-width: 100%;
					min-width: fit-content;
					min-height: 100%;
				}
			}

			.drawer::backdrop,
			.drawer[open].closing::backdrop {
				opacity: 0;
				background-color: color-mix( in srgb, var(--_cz-sidepanel-backdrop-color, black) 40%, transparent);
			}

			slot[name=close] {
				left: 0;
			}

			/*  animate  */
			@media screen and (prefers-reduced-motion: no-preference) {
				.drawer {
					transition-property: translate, display, overlay;
					transition-duration: var(--_cz-sidepanel-transition-duration);
					transition-timing-function: var(--_cz-sidepanel-transition-timing);
					transition-behavior: allow-discrete
				}
				.drawer::backdrop {
					transition-property: opacity, display, overlay;
					transition-duration: var(--_cz-sidepanel-transition-duration);
					transition-timing-function: var(--_cz-sidepanel-transition-timing);
					transition-behavior: allow-discrete
				}
			}

			.drawer.open {
				display: block;
			}

			.drawer[open] {
				--_translate-inline: calc(var(--_cz-sidepanel-width) * -1);
				box-shadow: color-mix( in srgb, var(--_cz-sidepanel-backdrop-color, black) 20%, transparent) var(--sidepanel-box-shadow-direction, -6px 0px 0px 0px);

				&::backdrop {
					opacity: 1;
				}	
			}

			@starting-style {
				.drawer[open]::backdrop {
					opacity: 0;		
				}
			}

			.content {
				padding-block: var(--_cz-sidepanel-content-padding-block, 0px 2rem);
				padding-inline: var(--_cz-sidepanel-content-padding-inline, 1rem);
			}

			slot[name=close] {
				display: inline-flex;
				position: sticky;
				top: 1rem;
				margin-bottom: 1rem;
				width: min-content;
				z-index: 10;
			}

			.closeButton {
				align-items: center;
				appearance: none;
				background-color: color-mix(in srgb, rgb(0 0 0) 0%, white);
				border-radius: var(--close-button-border-radius, 0 6px 6px 0);
				border: 1px solid;
				border-color: var(--close-button-border-color);
				align-self: baseline;
				display: inline-flex;
				cursor: pointer;
				font-weight: bold;
				justify-content: flex-start;
				overflow: hidden;
				transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
				vertical-align: top;
				column-gap: 6px;
				font-size: 18px;
				line-height: 22px;
				box-shadow: none;
				border-width: var(--close-button-border-width, 1px 1px 1px 0);
				align-self: baseline;
				padding: 13px 20px 13px 1rem;

				&:hover {
					color: var(--close-button-text-color-hover);
					background-color: color-mix(in srgb, rgb(0 0 0) 4%, white);
				}

				&:focus-visible {
					outline-color: var(--focus-ring-color);
					@supports (background-color: color-mix(in srgb, white, black)){
						outline-color: color-mix(in srgb, var(--focus-ring-color) 86%, transparent);
					}

					outline-style: solid;
					outline-width: 3px;

					outline-offset: 2px;
					@media screen and (prefers-reduced-motion: no-preference) {
						transition: outline-offset .125s linear;
					}
				}

				&:focus,
				&:active {
					color: var(--close-button-text-color-hover); 
					background-color: color-mix(in srgb, rgb(0 0 0) 12%, white);
				}

				& > span {
					text-wrap: nowrap;
				}

				& > svg {
					fill: currentcolor;
					width: 20px;
					height: 20px;
				}
			}
		}

		@layer variant {
			:host([wide]) {
				--cz-sidepanel-width: 100vi;
			}	

			:host([left]) {
				--translate-inline: calc(var(--_cz-sidepanel-width) * -1);
				--sidepanel-onleft: 100vi;
				--sidepanel-onright: 0px;
				--sidepanel-box-shadow-direction: 6px 0px 0px 0px;
				--close-button-border-width: 1px 0 1px 1px;
				--close-button-border-radius: 6px 0 0 6px;
			}

			:host([left]) .drawer[open]:not(.closing) {
				--_translate-inline: 0px;
			}

			:host([left]) slot[name=close] {
				right: 0;
				align-self: flex-end;
			}
		}
	`;

	static properties = {
		closeText: { type: String },
		open: { type: Boolean, reflect: true }
	};

	get dialog() {
		return this._dialog ??= this.renderRoot?.querySelector('dialog');
	}

	clickInsideDialog(node) {
		let result = false;
		const slotted = this.dialog?.querySelector('slot:not([name])')?.assignedNodes({ flatten: true });
		// if the node is not visible, or is slotted, or is inside a slotted node, it is considered to be inside the dialog
		result = !node.checkVisibility() || slotted.includes(node) || slotted.some((n) => n.contains(node));

		if (!result) {
			result = node.closest('.inner') === this.dialog?.firstElementChild;
		}
		return result;
	}

	constructor() {
		super();
		this.closeText = 'terug';
		this.open = false;
		this.focusTrap = trapFocus(this);
	}

	getExcludedSlots() {
		return ['trigger'];
	}

	getFocusableBefore() {
		const result = [];
		const closebtn = this.dialog?.querySelector('.closeButton');
		if (closebtn) result.push(closebtn);
		return result;
	}

	createRenderRoot() {
		const root = super.createRenderRoot();
		root.addEventListener(
			'click',
			(e) => {
				if (!this.open && !this.dialog?.open) {
					this.openSidepanel();
				} else if (this.open && this.dialog?.open && !this.clickInsideDialog(e.target)) {
					this.closeSidepanel();
				}
			});
		return root;
	}

	updated(changedProperties) {
		super.updated?.(changedProperties);
		if (this.open && !this.dialog?.open) {
			this.openSidepanel();
		}
	}

	evHandler = {
		backbuttonPress: (e) => this.onBackbuttonPress(e)
	}

	onBackbuttonPress(event) {
		if (event && event.state == 'backPressed' && this.open) {
			const shouldStayOpen = this.hasAttribute('stayOpenOnBack') && this.getAttribute('stayOpenOnBack').toLowerCase() === 'true';

			if (!shouldStayOpen) {
				this.closeSidepanel();
			}
		}
	}

	openSidepanel() {
		this.open = true;
		setTimeout(() => {
			this.dialog?.showModal();

			// Set the focus to the first focusable child of the dialog element
			this.focusTrap.setFocusToFirstItem();

			// Bind a focus event listener to the body element to make sure the focus
			// stays trapped inside the dialog while open, and start listening for some
			// specific key presses (TAB and ESC)
			document.body.addEventListener('focus', this.focusTrap.evHandlers.keepFocus, true);
			document.addEventListener('keydown', this.focusTrap.evHandlers.keyPress);

			if (window.history.state === 'showOverlay') {
				window.removeEventListener('popstate', this.evHandler.backbuttonPress);
			} else {
				if (window.history.state === 'backPressed') {
					window.removeEventListener('popstate', this.evHandler.backbuttonPress);
				} else {
					this._oldState = window.history.state;
					window.history.replaceState('backPressed', null, null);
				}
				window.history.pushState('showOverlay', null, null);
			}
			window.addEventListener('popstate', this.evHandler.backbuttonPress);
		}, 20);
	}

	closeSidepanel() {
		this.dialog?.classList.add('closing');
		setTimeout(() => {
			// check if not already closed like by esc key
			if (this.dialog?.open) {
				this.dialog?.close();
			} else {
				this.dialog?.classList.remove('closing');
				this.open = false;
			}
			// Remove the focus event listener to the body element and stop listening
			// for specific key presses
			document.body.removeEventListener('focus', this.focusTrap.evHandlers.keepFocus, true);
			document.removeEventListener('keydown', this.focusTrap.evHandlers.keyPress);
			window.removeEventListener('popstate', this.evHandler.backbuttonPress);

			if (window.history.state === 'backPressed' && !!this._oldState && this._oldState !== 'backPressed') {
				window.history.replaceState(this._oldState, null, null);
			}
		}, 300);
	}

	handleCancel(event) {
		event.preventDefault();
		this.closeSidepanel();
	}

	render() {
		return html`
		<slot name="trigger">
		</slot>
		<dialog class="drawer${this.open ? ' open' : ''}" @close=${()=> this.closeSidepanel()} @cancel=${(e) =>
			this.handleCancel(e)}>
			<div class=inner>
				<slot name=close @click=${()=> this.closeSidepanel()}>
					<button class=closeButton type=button>
						${this.hasAttribute('left') ? html`<span>${this.closeText}</span>${unsafeHTML(angleRight)}` :
						html`${unsafeHTML(angleLeft)}<span>${this.closeText}</span>`}
					</button>
				</slot>
				<div class=content>
					<slot></slot>
				</div>
			</div>
		</dialog>`;
	}
}
if (!customElements.get('cz-sidepanel')) {
	customElements.define('cz-sidepanel', CzSidepanel);
}

CSS.registerProperty?.({
	name: '--cz-sidepanel-width',
	syntax: '<length-percentage>',
	inherits: true,
	initialValue: '30%',
});